// ============================================
// Exploit Chain Visualizer
// ============================================

import {
  DEFENSE_EFFECTS,
  CHAIN_STAGES,
  getActiveDefenses,
  getEarliestBlock,
  type DefenseState,
} from '../../lib/labs/exploit-chain';

// ============================================
// UI Logic
// ============================================
const root = document.querySelector('[data-exploit-chain]') as HTMLElement | null;

if (root) {
  // DOM Elements
  const chainPlay = root.querySelector('#chain-play') as HTMLButtonElement;
  const chainStep = root.querySelector('#chain-step') as HTMLButtonElement;
  const chainReset = root.querySelector('#chain-reset') as HTMLButtonElement;
  const chainProgress = root.querySelector('#chain-progress') as HTMLElement;
  const chainProgressText = root.querySelector('#chain-progress-text') as HTMLElement;
  const chainStageTitle = root.querySelector('#chain-stage-title') as HTMLElement;
  const chainStageDesc = root.querySelector('#chain-stage-desc') as HTMLElement;
  const chainPayload = root.querySelector('#chain-payload code') as HTMLElement;
  const stackLocals = root.querySelector('#stack-locals') as HTMLElement;
  const stackCanary = root.querySelector('#stack-canary') as HTMLElement;
  const stackRbp = root.querySelector('#stack-rbp') as HTMLElement;
  const stackRip = root.querySelector('#stack-rip') as HTMLElement;

  // Defense elements
  const defenseBounds = root.querySelector('#defense-bounds') as HTMLInputElement;
  const defenseCanary = root.querySelector('#defense-canary') as HTMLInputElement;
  const defenseAslr = root.querySelector('#defense-aslr') as HTMLInputElement;
  const defenseDep = root.querySelector('#defense-dep') as HTMLInputElement;
  const defenseStatus = root.querySelector('#defense-status') as HTMLElement;
  const defenseImpact = root.querySelector('#defense-impact') as HTMLElement;
  const defenseImpactTitle = root.querySelector('#defense-impact-title') as HTMLElement;
  const defenseImpactDesc = root.querySelector('#defense-impact-desc') as HTMLElement;

  // State
  let chainStepIndex = -1;
  let chainPlaying = false;
  let chainInterval: ReturnType<typeof setInterval> | null = null;

  let defenses: DefenseState = {
    canary: false,
    aslr: false,
    dep: false,
    bounds: false,
  };

  function updateDefenseStatus() {
    const active = getActiveDefenses(defenses);
    if (active.length === 0) {
      defenseStatus.textContent = 'All defenses off';
      defenseStatus.classList.remove('border-emerald-500/50', 'text-emerald-500');
      defenseStatus.classList.add('border-border/70', 'text-muted');
    } else {
      defenseStatus.textContent = `${active.length} defense${active.length > 1 ? 's' : ''} active`;
      defenseStatus.classList.remove('border-border/70', 'text-muted');
      defenseStatus.classList.add('border-emerald-500/50', 'text-emerald-500');
    }

    const effect = getEarliestBlock(defenses);
    if (effect && chainStepIndex >= effect.blocksAt) {
      defenseImpact.classList.remove('hidden');
      defenseImpactTitle.textContent = effect.title;
      defenseImpactDesc.textContent =
        effect.desc + (effect.bypassNote ? ` (${effect.bypassNote})` : '');
    } else if (effect) {
      defenseImpact.classList.remove('hidden');
      defenseImpactTitle.textContent = 'Defense Active';
      defenseImpactDesc.textContent = `Exploit will be blocked at stage ${effect.blocksAt + 1}. Play the chain to see.`;
    } else {
      defenseImpact.classList.add('hidden');
    }
  }

  function updateChainVisualization() {
    const steps = root.querySelectorAll('.chain-step');
    const arrows = root.querySelectorAll('.chain-arrow');
    const effect = getEarliestBlock(defenses);
    const blockStage = effect ? effect.blocksAt : -1;

    steps.forEach((step, i) => {
      const node = step.querySelector('.chain-node') as HTMLElement;
      if (!node) return;
      node.classList.remove(
        'pending',
        'active',
        'complete',
        'danger',
        'blocked',
        'defended'
      );
      node.style.position = 'relative';

      if (blockStage >= 0 && i >= blockStage) {
        if (i === blockStage && chainStepIndex >= blockStage) {
          node.classList.add('defended');
        } else if (i > blockStage) {
          node.classList.add('blocked');
        } else {
          node.classList.add('pending');
        }
      } else if (i < chainStepIndex) {
        node.classList.add('complete');
      } else if (i === chainStepIndex) {
        node.classList.add(chainStepIndex >= 2 ? 'danger' : 'active');
      } else {
        node.classList.add('pending');
      }
    });

    arrows.forEach((arrow, i) => {
      arrow.classList.remove('active', 'complete', 'danger', 'blocked');

      if (blockStage >= 0 && i >= blockStage) {
        arrow.classList.add('blocked');
      } else if (i < chainStepIndex) {
        arrow.classList.add('complete');
      } else if (i === chainStepIndex - 1 && chainStepIndex > 0) {
        arrow.classList.add(chainStepIndex >= 3 ? 'danger' : 'active');
      }
    });

    // Update progress bar
    const progress =
      chainStepIndex >= 0 ? ((chainStepIndex + 1) / CHAIN_STAGES.length) * 100 : 0;
    chainProgress.style.width = `${progress}%`;
    chainProgressText.textContent = `Stage ${chainStepIndex + 1} of ${CHAIN_STAGES.length}`;

    if (chainStepIndex >= 0 && chainStepIndex < CHAIN_STAGES.length) {
      const stage = CHAIN_STAGES[chainStepIndex];

      if (effect && chainStepIndex >= effect.blocksAt) {
        chainStageTitle.textContent = effect.title;
        chainStageDesc.textContent = effect.desc;

        // Show defense-specific payload
        if (defenses.bounds && effect === DEFENSE_EFFECTS.bounds) {
          chainPayload.innerHTML = `<span class="code-comment">// DEFENSE: Bounds checking active</span>
<span class="code-type">char</span> buffer[<span class="code-num">16</span>];
<span class="code-type">size_t</span> input_len = <span class="code-fn">strlen</span>(user_input);

<span class="code-keyword">if</span> (input_len >= <span class="code-keyword">sizeof</span>(buffer)) {
    input_len = <span class="code-keyword">sizeof</span>(buffer) - <span class="code-num">1</span>;  <span class="code-comment">// Truncate!</span>
}
<span class="code-fn">strncpy</span>(buffer, user_input, input_len);
buffer[input_len] = <span class="code-str">'\\0'</span>;

<span class="code-comment">// ✓ Overflow prevented - input safely truncated</span>`;
        } else if (defenses.canary && effect === DEFENSE_EFFECTS.canary) {
          chainPayload.innerHTML = `<span class="code-comment">// DEFENSE: Stack canary detected corruption</span>
<span class="code-comment">// Function prologue placed canary on stack:</span>
<span class="code-comment">//   mov rax, fs:0x28     ; Load canary</span>
<span class="code-comment">//   mov [rbp-8], rax     ; Store on stack</span>

<span class="code-comment">// Overflow corrupted canary value...</span>
<span class="code-comment">// Function epilogue checks canary:</span>
<span class="code-keyword">mov</span> rax, [rbp-<span class="code-num">8</span>]        <span class="code-comment">; Load stored canary</span>
<span class="code-keyword">xor</span> rax, fs:<span class="code-num">0x28</span>       <span class="code-comment">; Compare with original</span>
<span class="code-keyword">jne</span> <span class="code-fn">__stack_chk_fail</span>  <span class="code-comment">; ✗ Mismatch! Abort!</span>

<span class="code-comment">// Program terminated before attacker gains control</span>`;
        } else if (
          (defenses.aslr && effect === DEFENSE_EFFECTS.aslr) ||
          (defenses.dep && effect === DEFENSE_EFFECTS.dep)
        ) {
          chainPayload.innerHTML = `<span class="code-comment">// DEFENSE: ${defenses.aslr ? 'ASLR' : 'DEP/NX'} prevents exploitation</span>
<span class="code-comment">// Attacker's return address: 0xdeadbeef</span>

${
  defenses.aslr
    ? `<span class="code-comment">// With ASLR enabled:</span>
<span class="code-comment">//   - Stack base: 0x7ff?????????  (randomized)</span>
<span class="code-comment">//   - libc base:  0x7f??????????  (randomized)</span>
<span class="code-comment">//   - Heap base:  0x5???????????? (randomized)</span>
<span class="code-comment">//</span>
<span class="code-comment">// Attacker cannot predict valid addresses!</span>
<span class="code-comment">// Jump to 0xdeadbeef → SIGSEGV (invalid memory)</span>`
    : `<span class="code-comment">// With DEP/NX enabled:</span>
<span class="code-comment">//   - Stack pages marked: rw- (no execute)</span>
<span class="code-comment">//   - Heap pages marked:  rw- (no execute)</span>
<span class="code-comment">//</span>
<span class="code-comment">// CPU attempts to execute shellcode on stack...</span>
<span class="code-comment">// Hardware exception → SIGSEGV (NX violation)</span>`
}

<span class="code-comment">// ✓ Exploit blocked - ${defenses.aslr ? 'address unknown' : 'execution prevented'}</span>`;
        }
      } else {
        chainStageTitle.textContent = stage.title;
        chainStageDesc.textContent = stage.desc;
        chainPayload.innerHTML = stage.payload;
      }

      stackLocals.innerHTML = stage.stack.locals;
      stackCanary.innerHTML = stage.stack.canary;
      stackRbp.innerHTML = stage.stack.rbp;
      stackRip.innerHTML = stage.stack.rip;

      const stackRows = root.querySelectorAll('.stack-row');
      stackRows.forEach((row) => {
        const rowName = row.getAttribute('data-row');
        row.classList.remove('corrupted', 'highlight');
        if (rowName && stage.corrupted.includes(rowName)) {
          row.classList.add('corrupted');
        }
        if (rowName && stage.highlight.includes(rowName)) {
          row.classList.add('highlight');
        }
      });
    } else {
      chainStageTitle.textContent = 'Waiting to start';
      chainStageDesc.textContent =
        'Click "Play Chain" to step through a stack buffer overflow exploit, or use "Step" to advance one stage at a time.';
      chainPayload.innerHTML =
        '<span class="text-slate-400">// Payload will appear here during exploit chain</span>';
      stackLocals.textContent = '[ buffer[16] ]';
      stackCanary.textContent = '0xDEADBEEF';
      stackRbp.textContent = '0x7fff1234';
      stackRip.textContent = '0x401156';
      const stackRows = root.querySelectorAll('.stack-row');
      stackRows.forEach((row) => row.classList.remove('corrupted', 'highlight'));
    }

    updateDefenseStatus();
  }

  function playChainStepFn() {
    if (chainStepIndex >= CHAIN_STAGES.length - 1) {
      stopChain();
      return;
    }
    chainStepIndex++;
    updateChainVisualization();
  }

  function stopChain() {
    chainPlaying = false;
    if (chainInterval) {
      clearInterval(chainInterval);
      chainInterval = null;
    }
    chainPlay.textContent =
      chainStepIndex >= CHAIN_STAGES.length - 1 ? 'Replay' : 'Play Chain';
  }

  function resetChain() {
    stopChain();
    chainStepIndex = -1;
    updateChainVisualization();
    chainPlay.textContent = 'Play Chain';
  }

  function startChain() {
    chainPlaying = true;
    chainPlay.textContent = 'Pause';
    chainInterval = setInterval(playChainStepFn, 2500);
    playChainStepFn();
  }

  // Event Listeners
  chainPlay?.addEventListener('click', () => {
    if (chainStepIndex >= CHAIN_STAGES.length - 1) {
      resetChain();
      setTimeout(startChain, 300);
    } else if (chainPlaying) {
      stopChain();
    } else {
      startChain();
    }
  });

  chainStep?.addEventListener('click', () => {
    stopChain();
    if (chainStepIndex >= CHAIN_STAGES.length - 1) {
      resetChain();
    } else {
      chainStepIndex++;
      updateChainVisualization();
    }
  });

  chainReset?.addEventListener('click', resetChain);

  // Defense listeners
  defenseBounds?.addEventListener('change', () => {
    defenses.bounds = defenseBounds.checked;
    updateDefenseStatus();
    updateChainVisualization();
  });

  defenseCanary?.addEventListener('change', () => {
    defenses.canary = defenseCanary.checked;
    updateDefenseStatus();
    updateChainVisualization();
  });

  defenseAslr?.addEventListener('change', () => {
    defenses.aslr = defenseAslr.checked;
    updateDefenseStatus();
    updateChainVisualization();
  });

  defenseDep?.addEventListener('change', () => {
    defenses.dep = defenseDep.checked;
    updateDefenseStatus();
    updateChainVisualization();
  });

  // Keyboard shortcuts
  document.addEventListener('keydown', (e) => {
    if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement)
      return;

    if (e.key === ' ' || e.key === 'Spacebar') {
      e.preventDefault();
      chainPlay?.click();
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      chainStep?.click();
    } else if (e.key === 'r' || e.key === 'R') {
      e.preventDefault();
      chainReset?.click();
    } else if (e.key === '1') {
      e.preventDefault();
      defenseBounds.checked = !defenseBounds.checked;
      defenseBounds.dispatchEvent(new Event('change'));
    } else if (e.key === '2') {
      e.preventDefault();
      defenseCanary.checked = !defenseCanary.checked;
      defenseCanary.dispatchEvent(new Event('change'));
    } else if (e.key === '3') {
      e.preventDefault();
      defenseAslr.checked = !defenseAslr.checked;
      defenseAslr.dispatchEvent(new Event('change'));
    } else if (e.key === '4') {
      e.preventDefault();
      defenseDep.checked = !defenseDep.checked;
      defenseDep.dispatchEvent(new Event('change'));
    }
  });

  // Initialize
  updateChainVisualization();
}
